import React from 'react';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody, Dialog, DialogHeader, DialogBody, DialogFooter } from '@repo/ui';
import type { WebhookSubscription } from '../types';

interface ClioInspectionModalProps {
  open: boolean;
  onClose: () => void;
  inspectionData: {
    success: boolean;
    webhooks: any[];
    message: string;
  } | undefined;
  isLoading: boolean;
  localWebhooks: WebhookSubscription[];
  onDeleteClick: (webhook: any, action: string) => void;
  onRefresh: () => void;
}

export const ClioInspectionModal: React.FC<ClioInspectionModalProps> = ({
  open,
  onClose,
  inspectionData,
  isLoading,
  localWebhooks,
  onDeleteClick,
  onRefresh,
}) => {
  return (
    <Dialog open={open} onClose={onClose} size="xl" ariaLabel="Clio Webhook Inspection">
      <DialogHeader>
        <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
          Clio Webhook Inspection
        </h2>
        <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
          View webhook configuration details directly from Clio API
        </p>
      </DialogHeader>

      <DialogBody className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-xtnd-blue mx-auto"></div>
            <p className="mt-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">
              Loading Clio webhook configuration...
            </p>
          </div>
        ) : inspectionData?.success ? (
          <div className="space-y-4">
            {inspectionData.webhooks.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="HiExclamation" className="mx-auto h-12 w-12 text-xtnd-dark-300" />
                <p className="mt-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">No webhooks configured in Clio</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center">
                    <Icon name="HiCog" className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                    <span className="font-medium text-blue-900 dark:text-blue-100">
                      Found {inspectionData.webhooks.length} webhook(s) in Clio
                    </span>
                  </div>
                </div>

                {inspectionData.webhooks.map((webhook: any, index: number) => {
                  const localWebhook = localWebhooks.find(
                    w => w.clioWebhookId === webhook.id.toString()
                  );
                  const isOrphaned = !localWebhook;

                  return (
                    <Card key={index} className="border border-xtnd-dark-100 dark:border-xtnd-dark-600">
                      <CardHeader className="pb-2 flex flex-row justify-between items-start">
                        <div>
                          <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
                            {webhook.model?.toUpperCase()} Webhook
                          </h3>
                          <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">ID: {webhook.id}</p>
                        </div>
                        <Button
                          onClick={() =>
                            onDeleteClick(webhook, isOrphaned ? 'delete_clio_only' : 'delete')
                          }
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                          leftIcon={<Icon name="HiTrash" size="sm" />}
                        >
                          {isOrphaned ? 'Delete from Clio Only' : 'Delete Everything'}
                        </Button>
                      </CardHeader>
                      <CardBody>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm overflow-x-auto">
                          <div className="min-w-0">
                            <strong className="text-xtnd-dark dark:text-xtnd-white">URL:</strong>
                            <p className="mt-1 text-xs font-mono bg-xtnd-dark-50 dark:bg-xtnd-dark-700 p-2 rounded break-all max-w-full">
                              {webhook.url}
                            </p>
                          </div>
                          <div>
                            <strong className="text-xtnd-dark dark:text-xtnd-white">Model:</strong>
                            <p className="mt-1 text-xtnd-dark-600 dark:text-xtnd-dark-200">{webhook.model}</p>
                          </div>
                          <div>
                            <strong className="text-xtnd-dark dark:text-xtnd-white">Events:</strong>
                            <p className="mt-1 text-xtnd-dark-600 dark:text-xtnd-dark-200 break-words">
                              {Array.isArray(webhook.events)
                                ? webhook.events.join(', ')
                                : webhook.events}
                            </p>
                          </div>
                          <div>
                            <strong className="text-xtnd-dark dark:text-xtnd-white">Fields:</strong>
                            <p className="mt-1 text-xtnd-dark-600 dark:text-xtnd-dark-200 break-words">
                              {Array.isArray(webhook.fields)
                                ? webhook.fields.join(', ')
                                : webhook.fields}
                            </p>
                          </div>
                          <div>
                            <strong className="text-xtnd-dark dark:text-xtnd-white">Status:</strong>
                            <span
                              className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                webhook.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : webhook.status === 'inactive'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-xtnd-dark-50 text-xtnd-dark-700'
                              }`}
                            >
                              {webhook.status}
                            </span>
                          </div>
                          <div>
                            <strong className="text-xtnd-dark dark:text-xtnd-white">Created:</strong>
                            <p className="mt-1 text-xtnd-dark-600 dark:text-xtnd-dark-200">
                              {webhook.created_at ? new Date(webhook.created_at).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <strong className="text-xtnd-dark dark:text-xtnd-white">Updated:</strong>
                            <p className="mt-1 text-xtnd-dark-600 dark:text-xtnd-dark-200">
                              {webhook.updated_at ? new Date(webhook.updated_at).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <strong className="text-xtnd-dark dark:text-xtnd-white">Expires:</strong>
                            <p className="mt-1 text-xtnd-dark-600 dark:text-xtnd-dark-200">
                              {webhook.expires_at ? new Date(webhook.expires_at).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          {isOrphaned && (
                            <div className="col-span-full">
                              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                                <div className="flex items-center">
                                  <Icon
                                    name="HiExclamation"
                                    className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2"
                                  />
                                  <span className="text-sm text-yellow-900 dark:text-yellow-100">
                                    Orphaned webhook - not found in local configuration
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="HiExclamation" className="mx-auto h-12 w-12 text-red-400" />
            <p className="mt-2 text-red-600 dark:text-red-400">
              {inspectionData?.message || 'Failed to load Clio webhook configuration'}
            </p>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button onClick={onRefresh} variant="outline" size="sm" leftIcon={<Icon name="HiRefresh" size="sm" />}>
          Refresh
        </Button>
        <Button onClick={onClose} variant="primary">
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

